import type { PeerInfo, PeerView, RoomInfo } from '@flowshare/protocol';
import { create } from 'zustand';

export type SessionPhase =
  | 'idle'
  | 'creating'
  | 'joining'
  | 'connected'
  | 'reconnecting'
  | 'ended'
  | 'error';

export interface SessionError {
  code: string;
  message: string;
}

interface SessionStore {
  phase: SessionPhase;
  room: RoomInfo | null;
  self: PeerInfo | null;
  peers: Record<string, PeerView>;
  hostToken: string | null;
  error: SessionError | null;
  latencyMs: number | null;
  joinedAt: number | null;
  start: (phase: 'creating' | 'joining') => void;
  setConnected: (room: RoomInfo, self: PeerInfo, peers: PeerInfo[]) => void;
  setPhase: (phase: SessionPhase) => void;
  upsertPeer: (peer: PeerInfo) => void;
  patchPeer: (peerId: string, patch: Partial<PeerView>) => void;
  removePeer: (peerId: string) => void;
  patchRoom: (patch: Partial<RoomInfo>) => void;
  setHostToken: (token: string | null) => void;
  setError: (error: SessionError | null) => void;
  setLatency: (latency: number | null) => void;
  reset: () => void;
}

const initial = {
  phase: 'idle' as SessionPhase,
  room: null,
  self: null,
  peers: {},
  hostToken: null,
  error: null,
  latencyMs: null,
  joinedAt: null,
};

export const useSessionStore = create<SessionStore>()((set) => ({
  ...initial,
  start: (phase) => set({ ...initial, phase }),
  setConnected: (room, self, peers) =>
    set((state) => {
      const now = Date.now();
      const next: Record<string, PeerView> = {};
      for (const peer of peers) {
        const existing = state.peers[peer.id];
        next[peer.id] = {
          ...peer,
          connectionState: peer.id === self.id ? 'connected' : (existing?.connectionState ?? 'idle'),
          connectedAt: peer.id === self.id ? now : (existing?.connectedAt ?? null),
          latencyMs: existing?.latencyMs ?? null,
          isSelf: peer.id === self.id,
        };
      }
      next[self.id] = {
        ...self,
        connectionState: 'connected',
        connectedAt: now,
        latencyMs: 0,
        isSelf: true,
      };

      return {
        phase: 'connected',
        room,
        self,
        peers: next,
        error: null,
        joinedAt: state.joinedAt ?? now,
      };
    }),
  setPhase: (phase) => set({ phase }),
  upsertPeer: (peer) =>
    set((state) => ({
      peers: {
        ...state.peers,
        [peer.id]: {
          ...peer,
          connectionState:
            peer.id === state.self?.id
              ? 'connected'
              : (state.peers[peer.id]?.connectionState ?? 'connecting'),
          connectedAt:
            peer.id === state.self?.id
              ? (state.peers[peer.id]?.connectedAt ?? Date.now())
              : (state.peers[peer.id]?.connectedAt ?? null),
          latencyMs: state.peers[peer.id]?.latencyMs ?? null,
          isSelf: peer.id === state.self?.id,
        },
      },
    })),
  patchPeer: (peerId, patch) =>
    set((state) => {
      const existing = state.peers[peerId];
      if (!existing) return state;
      return {
        peers: { ...state.peers, [peerId]: { ...existing, ...patch, isSelf: peerId === state.self?.id } },
      };
    }),
  removePeer: (peerId) =>
    set((state) => {
      if (peerId === state.self?.id) return state;
      const next = { ...state.peers };
      delete next[peerId];
      return { peers: next };
    }),
  patchRoom: (patch) =>
    set((state) => (state.room ? { room: { ...state.room, ...patch } } : state)),
  setHostToken: (hostToken) => set({ hostToken }),
  setError: (error) => set({ error, phase: error ? 'error' : 'idle' }),
  setLatency: (latencyMs) => set({ latencyMs }),
  reset: () => set({ ...initial }),
}));

export function resetSession(): void {
  useSessionStore.getState().reset();
}
